import React, { useEffect, useRef } from "react";
import { useFormikContext } from "formik";

/**
 * Create Certificate with a mistake on the form (owner, 2026-11-04: "validation error iruntha antha position ku auto scroll
 * aganum, which field is first"): brings the FIRST field showing an error into view, Brisk and RoadZen alike.
 *
 * "First" = page order: the first `.errorMessage` box under `root` that shows a message (every field on these forms puts
 * its message there; an empty box holds a space). That box's parent is the field's own box: it goes to the middle of the
 * window (the navbar is on top, the button bar at the bottom) and its input gets the cursor without a second jump.
 * Returns true when it found one.
 */
export const scrollToFirstError = (root) => {
  if (!root || typeof root.querySelectorAll !== "function") return false;
  const shown = Array.from(root.querySelectorAll(".errorMessage")).find((el) => el.textContent.trim());
  if (!shown) return false;
  const box = shown.parentElement || shown;
  if (typeof box.scrollIntoView === "function") box.scrollIntoView({ behavior: "smooth", block: "center" });
  const input = box.querySelector("input:not([type=hidden]):not([type=file]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role=combobox]");
  if (input && typeof input.focus === "function") {
    try {
      input.focus({ preventScroll: true });
    } catch (e) {
      /* an old browser without focus options: the scroll above already showed it */
    }
  }
  return true;
};

/** After the next paint (the messages are drawn by then), scrolls to the first error under `root`. */
export const scrollToFirstErrorSoon = (root) => {
  setTimeout(() => scrollToFirstError(root), 0);
};

/**
 * Put inside a Formik <Form>: when a submit is refused by the form's checks, scrolls to the first field with an error.
 * A submit that passes (or one still running) does nothing. Renders a hidden marker to find its own form.
 */
export const ScrollToFirstError = () => {
  const { submitCount, isSubmitting, isValidating, errors } = useFormikContext();
  const marker = useRef(null);
  const handled = useRef(0);
  useEffect(() => {
    if (!submitCount || submitCount === handled.current || isSubmitting || isValidating) return;
    handled.current = submitCount;
    if (!errors || !Object.keys(errors).length) return;
    const form = marker.current && marker.current.closest("form");
    scrollToFirstErrorSoon(form || document);
  }, [submitCount, isSubmitting, isValidating, errors]);
  return <span ref={marker} hidden data-testid="scroll-to-first-error" />;
};
